import { Study } from 'interfaces/study';
import { isEqual } from 'lodash';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { StudyWorkflowConfiguration, getStudyWorkflowConfiguration } from '../../Workflow/studyWorkflowConfiguration';
import { WorkflowStageSummary } from './workflowMonitorModel';

export interface MultiplexWorkflowOverview {
  currentStage?: WorkflowStageSummary;
  recommendedActionStage?: WorkflowStageSummary;
  isLoading: boolean;
  hasScope: boolean;
}

interface MultiplexWorkflowContextValue {
  configuration: StudyWorkflowConfiguration;
  overview: MultiplexWorkflowOverview;
  setOverview: React.Dispatch<React.SetStateAction<MultiplexWorkflowOverview>>;
}

const MultiplexWorkflowContext = createContext<MultiplexWorkflowContextValue | undefined>(undefined);

interface MultiplexWorkflowProviderProps {
  study?: Study;
}

export const MultiplexWorkflowProvider: React.FC<React.PropsWithChildren<MultiplexWorkflowProviderProps>> = ({
  children,
  study,
}) => {
  const configuration = useMemo(
    () => getStudyWorkflowConfiguration(study?.settings?.workflow?.modules),
    [study?.settings?.workflow?.modules]
  );
  const [overview, setOverviewState] = useState<MultiplexWorkflowOverview>({ isLoading: false, hasScope: false });

  const setOverview = useCallback<React.Dispatch<React.SetStateAction<MultiplexWorkflowOverview>>>((nextOverview) => {
    setOverviewState((currentOverview) => {
      const resolvedOverview = typeof nextOverview === 'function' ? nextOverview(currentOverview) : nextOverview;

      return isEqual(currentOverview, resolvedOverview) ? currentOverview : resolvedOverview;
    });
  }, []);

  const value = useMemo<MultiplexWorkflowContextValue>(
    () => ({
      configuration,
      overview,
      setOverview,
    }),
    [configuration, overview]
  );

  return <MultiplexWorkflowContext.Provider value={value}>{children}</MultiplexWorkflowContext.Provider>;
};

export const useMultiplexWorkflow = () => {
  const context = useContext(MultiplexWorkflowContext);

  if (!context) {
    throw new Error('useMultiplexWorkflow must be used within MultiplexWorkflowProvider');
  }

  return context;
};
